import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { GetVotingInformationBasis, GetVotingInformationBasisVariables } from "src/api/generated/GetVotingInformationBasis";
import {
    InsertVotingInformationBasis,
    InsertVotingInformationBasisVariables
} from "src/api/generated/InsertVotingInformationBasis";
import {
    DELETE_VOTING,
    GET_VOTING_DETAILS,
    GET_VOTING_INFORMATION_BASIS,
    GET_VOTING_OPTIONS,
    GET_VOTING_TENANTS_OPTIONS_RESULTS,
    GET_VOTINGS_BY_AGENDAITEMID,
    INSERT_VOTING_INFORMATION_BASIS,
    INSERT_VOTING_OPTION_AGENDAITEM,
    UPDATE_VOTETYPE_VOTINGS,
    UPSERT_VOTING_AGENDA_RESULTS
} from "src/api/voting";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { RootStore } from "src/stores/RootStore";
import { MessageType } from "src/components/notifications/Notifier";
import { v4 as uuidv4 } from "uuid";
import i18next from "i18next";
import {
    InsertVotingOptions,
    InsertVotingOptionsVariables
} from "src/api/generated/InsertVotingOptions";
import { DeleteVoting, DeleteVotingVariables } from "src/api/generated/DeleteVoting";
import {
    GetVotingsByAgendaitemId,
    GetVotingsByAgendaitemIdVariables
} from "src/api/generated/GetVotingsByAgendaitemId";
import {
    GetVotingTenantsAndOptionsAndResults,
    GetVotingTenantsAndOptionsAndResultsVariables
} from "src/api/generated/GetVotingTenantsAndOptionsAndResults";
import {
    UpsertVotingAgendaitemResult,
    UpsertVotingAgendaitemResultVariables
} from "src/api/generated/UpsertVotingAgendaitemResult";
import { GetVotingDetails, GetVotingDetailsVariables } from "src/api/generated/GetVotingDetails";
import { GetVotingOptions, GetVotingOptionsVariables } from "src/api/generated/GetVotingOptions";
import { survey_voting_mls_constraint, survey_voting_mls_update_column } from "src/api/generated/globalTypes";
import { IAttendanceState } from "./AttendanceListStore";
import { UpdateVotetypeVotings, UpdateVotetypeVotingsVariables } from "src/api/generated/UpdateVotetypeVotings";
import Axios from "src/network/Axios";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey } from "src/network/User";
import { ENUM_TYPE } from "src/stores/EnumerationStore";
import { getLang } from "src/utils/language";

interface IKeys {
    keyid: string;
    name: string;
    type: number;
}

export enum QuorumType {
    SIMPLE_MAJORITY = 0,
    QUALIFIED_MAJORITY = 1,
    UNANIMITY = 2
}

export enum VoteType {
    ARBITRAMENT = 0,
    SELECTION_VOTE = 1
}

const getArbitramentOptions = () => [i18next.t("labels.yes"), i18next.t("labels.no"), i18next.t("labels.abstrain")];
export interface IVotingBasis {
    votingId: string;
    title: string;
    question: string;
    quorum: QuorumType;
    key1: string;
    key2?: string;
    veto: boolean;
    votingMlsId: string;
    votetype: number;
    majority: number;
    entirety: boolean;
}

export interface IVoting {
    votingid: string;
    votingagendaitemid: string;
    question: string;
    title: string;
}

export interface IVotingTenant {
    tenantid: string;
    name: string;
    unitvalue: number;
    unitcount: number;
}

export interface IVotingOptions {
    votingoptionid: string;
    text: string;
    sort?: number | null;
}

export interface IVotingResults {
    id: string;
    state: number;
    unitid: string;
    votingagendaitemrealestateid: string;
    tenantid: string;
    votingoptionid: string | null;
}

export interface IVotingOptionResult {
    votingoptionid: string;
    votingoptiondresult: number;
}

export interface IDisplayedVotingResult {
    votingagendaitemid: string;
    resultText: string;
    resultid: string | null;
    votingaccepted: boolean;
    key1accepted: boolean;
    key2accepted: boolean;
    key1details: IVotingOptionResult[] | null;
    key2details: IVotingOptionResult[] | null;
}

export interface IPieChartProps {
    label: string;
    color: string;
    number: any;
}

export interface ITenantForQuorumCheck {
    state: number;
    tenantid: string;
}

export class VotingStore {
    rootStore: RootStore;
    isVotingModalDisplayed: boolean = false;

    quorums: Map<number, { label: string }> = new Map();
    keys: IKeys[] = [];

    votingBasis: IVotingBasis = {
        votingId: "",
        title: "",
        question: "",
        quorum: QuorumType.SIMPLE_MAJORITY,
        key1: "",
        key2: "",
        veto: false,
        votingMlsId: "",
        votetype: VoteType.ARBITRAMENT,
        majority: 51,
        entirety: false
    };

    votingList: IVoting[] = [];
    selectedVoting: IVoting = {
        votingid: "",
        votingagendaitemid: "",
        title: "",
        question: ""
    };

    votingOptionsDefault: string[] = getArbitramentOptions();
    selectedRealestateIds: string[] = [];

    lastLoadedAgendaitemId: string | null = "";
    lastLoadedVotingContextKey: string = "";

    votingTenants: IVotingTenant[] = [];
    votingOptions: IVotingOptions[] = [];
    votingResults: IVotingResults[] = [];

    displayedVotingResult: IDisplayedVotingResult = {
        votingagendaitemid: this.selectedVoting.votingagendaitemid || "",
        resultText: "",
        resultid: null,
        votingaccepted: true,
        key1accepted: true,
        key2accepted: true,
        key1details: null,
        key2details: null
    };

    votingComponentResponse: { id: string; selectedColumn: number | null }[] = [];

    isNewVoting: boolean = true;
    selectedVotingId: string = "";
    selectedVotingRefreshKey: number = 0;
    votingColors: string[] = ["#004997", "#900000", "#f8a300", "#194d00", "#650097"];
    quorumMls: string = "";
    key1Mls: string = "";
    key2Mls: string = "";
    votingResultTextArbitrament: string = "";
    isVotingResultTie: boolean = false;
    votingResultText: string = "";
    isVotingAccepted: boolean = true;
    hasNoVotingOptions: boolean = true;
    isQuoratePerVoting: boolean = true;
    tenantListForQuorumCheck: ITenantForQuorumCheck[] = [];

    isVotingOptionsModified: boolean = false;

    validationErrorMajority: string = "";

    validateMajority = () => {
        const value = this.votingBasis.majority;

        if (value < 20 || value > 100) {
            this.validationErrorMajority = i18next.t("screens.assemblies.vote.majority_range_error");
        } else {
            this.validationErrorMajority = "";
        }
    };

    clearValidationError = () => {
        this.validationErrorMajority = "";
    };

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            isVotingModalDisplayed: observable,
            quorums: observable,
            keys: observable,
            votingBasis: observable,
            votingOptionsDefault: observable,
            selectedRealestateIds: observable,
            votingList: observable,
            selectedVoting: observable,
            lastLoadedAgendaitemId: observable,
            lastLoadedVotingContextKey: observable,
            votingTenants: observable,
            votingOptions: observable,
            votingComponentResponse: observable,
            votingResults: observable,
            isNewVoting: observable,
            selectedVotingId: observable,
            selectedVotingRefreshKey: observable,
            votingColors: observable,
            quorumMls: observable,
            key1Mls: observable,
            key2Mls: observable,
            isVotingResultTie: observable,
            votingResultText: observable,
            isVotingAccepted: observable,
            validationErrorMajority: observable,
            isVotingOptionsModified: observable,
            displayedVotingResult: observable,
            hasNoVotingOptions: observable,
            isQuoratePerVoting: observable,

            hasVotingResultChanged: computed,
            pieChartList: computed,
            pieChartOuterList: computed,
            votingColorResult: computed,
            isTitleAndQuestionFilledOut: computed,
            isVotingBasisValid: computed,
            isRealestateSelected: computed,
            tenantRows: computed,

            loadVotingInformationBasis: action,
            setIsVotingModalDisplayed: action,
            setQuorums: action,
            setKeys: action,
            updateQuorum: action,
            updateKey: action,
            updateVeto: action,
            updateEntirety: action,
            updateVotingBasisField: action,
            resetVotingBasis: action,
            saveVotingInformationBasis: action,
            setVotingOptionsDefault: action,
            setVotingOptions: action,
            setSelectedRealestateIds: action,
            saveVotingOptions: action,
            deleteVotingById: action,
            resetAllVotingData: action,
            resetVotingLoadCache: action,
            loadAllVotingsByAgendaItemId: action,
            setSelectedVoting: action,
            loadVotingTenantsAndOptions: action,
            saveVotingResult: action,
            setVotingComponentResponse: action,
            setIsNewVoting: action,
            loadVotingDetails: action,
            setSelectedVotingId: action,
            loadVotingOptions: action,
            setQuorumAndKeysMls: action,
            setIsVotingResultTie: action,
            setVotingResultText: action,
            setIsVotingAccepted: action,
            setTenantActive: action,
            validateMajority: action,
            clearValidationError: action,
            clearLiveVotingData: action,
            getVotingResult: action,
            setHasNoVotingOptions: action,
            setIsQuoratePerVoting: action,
            getVotingAgendaitemQuorumState: action,
            calcVotingAgendaitemQuorumState: action,
            setTenantListForQuorumCheck: action,
            setLastLoadedVotingContextKey: action
        });
    }

    /* COMPUTED */

    get tenantRows() {
        return this.votingTenants.map((tenant) => {
            const attendance = this.rootStore.attendanceListStore.assemblyAttendances.find(
                (a) => a.tenantid === tenant.tenantid
            );

            // baseline from attendance
            let active = attendance ? attendance.attendancestate !== IAttendanceState.ABSENT : true;

            // aggregate all votingResults for this tenant
            const results = this.votingResults.filter((v) => v.tenantid === tenant.tenantid);
            if (results.length) {
                const anyPresent = results.some(
                    (v) => v.state === IAttendanceState.PRESENT || v.state === IAttendanceState.REPRESENTED
                );
                const anyAbsent = results.some((v) => v.state === IAttendanceState.ABSENT);

                if (anyPresent && !anyAbsent) active = true;
                else if (anyAbsent && !anyPresent) active = false;
                else active = false;
            }

            return {
                id: tenant.tenantid,
                label: tenant.name,
                selectedRow: this.votingComponentResponse.find((r) => r.id === tenant.tenantid)?.selectedColumn ?? 0,
                active,
                unitvalue: tenant.unitvalue,
                unitcount: tenant.unitcount
            };
        });
    }

    get hasVotingResultChanged(): boolean {
        const selectionChanged = this.votingComponentResponse.some(({ id, selectedColumn }) => {
            const original = this.votingResults.find((r) => r.tenantid === id);
            const originalOptionId = original?.votingoptionid;
            const originalIndex = originalOptionId
                ? this.votingOptions.findIndex((opt) => opt.votingoptionid === originalOptionId)
                : -1;

            const originalSelected = originalIndex >= 0 ? originalIndex + 1 : null;
            return originalSelected !== selectedColumn;
        });
        if (selectionChanged) return true;

        for (const tenant of this.votingTenants) {
            const tenantId = tenant.tenantid;

            // Baseline from attendance (what "active" would be without any voting override)
            const attendance = this.rootStore.attendanceListStore.assemblyAttendances.find(
                (a) => a.tenantid === tenantId
            );
            const baselineActive = attendance ? attendance.attendancestate !== IAttendanceState.ABSENT : true;

            // Derive an override from ALL votingResults for this tenant
            const results = this.votingResults.filter((v) => v.tenantid === tenantId);
            if (results.length) {
                const allPresent = results.every((r) => r.state === IAttendanceState.PRESENT);
                const allAbsent = results.every((r) => r.state === IAttendanceState.ABSENT);

                // If all rows agree, we have a clear override; if mixed, treat as a change.
                if (allPresent && baselineActive !== true) return true;
                if (allAbsent && baselineActive !== false) return true;
                if (!allPresent && !allAbsent) return true; // mixed -> changed
            }
        }

        return false;
    }

    get isTitleAndQuestionFilledOut(): boolean {
        return this.votingBasis.title.length > 0 && this.votingBasis.question.length > 0;
    }

    get isVotingBasisValid(): boolean {
        const { majority } = this.votingBasis;
        const isMajorityInRange = majority >= 20 && majority <= 100 && this.validationErrorMajority === "";
        return this.isTitleAndQuestionFilledOut && isMajorityInRange;
    }

    get isRealestateSelected(): boolean {
        return this.selectedRealestateIds.length > 0;
    }

    getQualifiedMajorityHtml(accepted: boolean, mls: string): string {
        const tKey = accepted
            ? "screens.assemblies.vote.required_majority"
            : "screens.assemblies.vote.not_required_majority";

        return i18next.t(tKey, { keys: mls });
    }

    private buildPieChartList(basisKey: string): IPieChartProps[] {
        const tenantsInformation = this.tenantRows;

        // don't run until both keys and options are loaded
        if (this.keys.length < 2 || this.votingOptions.length === 0) {
            return [];
        }

        // type 1 = Wertquote (weighted by unitvalue)
        // type 2 = Objektstimmen (weighted by unit count)
        // type 3 = Kopfstimmen (1 per tenant)
        const keyType = this.keys.find((k) => k.keyid === basisKey)?.type ?? 0;
        const isWertquote = keyType === 1;
        const isObjektstimmen = keyType === 2;

        // only ACTIVE tenants participate (only they can cast a vote)
        const activeTenants = tenantsInformation.filter((t) => t.active === true);

        // calculation basis for percentages: everyone (entirety) vs. only the present/active tenants
        const basisTenants = this.votingBasis.entirety ? tenantsInformation : activeTenants;

        const unitValueMap = isWertquote ? new Map(activeTenants.map((t) => [t.id, t.unitvalue])) : undefined;
        const unitCountMap = isObjektstimmen ? new Map(activeTenants.map((t) => [t.id, t.unitcount])) : undefined;

        const activeIds = new Set(activeTenants.map((t) => t.id));
        const votedIds = new Set<string>();

        const totals = new Array(this.votingOptions.length).fill(0);
        for (const { id, selectedColumn } of this.votingComponentResponse) {
            if (!selectedColumn || selectedColumn < 1) continue;
            const idx = selectedColumn - 1;
            if (idx >= totals.length) continue;
            if (!activeIds.has(id)) continue;

            votedIds.add(id);

            totals[idx] += isWertquote
                ? (unitValueMap!.get(id) ?? 0)
                : isObjektstimmen
                  ? (unitCountMap!.get(id) ?? 0)
                  : 1;
        }

        const slices: IPieChartProps[] = this.votingOptions.map((opt, i) => ({
            label: opt.text,
            color: this.votingColors?.[i] ?? "#ccc",
            number: totals[i]
        }));

        // add "missing / not voted" slice - basis includes absent tenants too when entirety is enabled,
        // so the pie chart's percentages are computed against the total ownership, not just those present
        const missing = basisTenants.reduce((sum, t) => {
            if (votedIds.has(t.id)) return sum;
            return sum + (isWertquote ? t.unitvalue : isObjektstimmen ? t.unitcount : 1);
        }, 0);

        if (missing > 0) {
            slices.push({
                label: i18next.t("screens.assemblies.vote.not_voted"),
                color: "#878786",
                number: missing
            });
        }
        return slices;
    }

    /** one‐tenant–one‐vote or weighted by key1 */
    get pieChartList(): IPieChartProps[] {
        return this.buildPieChartList(this.votingBasis.key1);
    }

    /** same logic but use key2 as the basis */
    get pieChartOuterList(): IPieChartProps[] {
        return this.buildPieChartList(this.votingBasis.key2!);
    }

    get votingColorResult(): string {
        if (this.votingResults.length === 0) {
            return "#ccc";
        }
        const winningOption = this.votingOptions.find(
            (opt) =>
                this.displayedVotingResult.resultid?.startsWith(opt.votingoptionid) ||
                this.displayedVotingResult.resultid?.includes(opt.votingoptionid)
        );
        if (winningOption) {
            const index = this.votingOptions.findIndex((opt) => opt.votingoptionid === winningOption.votingoptionid);
            return this.votingColors[index % this.votingColors.length]; // cycle through colors if more options than colors
        }

        return "#ccc";
    }

    /* ACTIONS */

    getVotingAgendaitemQuorumState = async () => {
        try {
            const response = await Axios.getAuthInstance().get(
                NetworkConfig.getVotingAgendaitemQuorumState + this.selectedVoting.votingagendaitemid,
                {
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                }
            );

            runInAction(() => {
                this.setIsQuoratePerVoting(response.data.isQuorumSatisfied);
            });
        } catch (error) {
            console.error("Error while trying to call getVotingAgendaitemQuorumState: ", error);
        }
    };

    calcVotingAgendaitemQuorumState = async () => {
        try {
            const bodyTenantList = {
                tenantlist: this.tenantListForQuorumCheck.map((tenant) => ({
                    state: tenant.state,
                    tenantid: tenant.tenantid
                }))
            };

            const response = await Axios.getAuthInstance().post(
                NetworkConfig.calcVotingAgendaitemQuorumState + this.selectedVoting.votingagendaitemid,
                bodyTenantList,
                {
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                }
            );

            runInAction(() => {
                this.setIsQuoratePerVoting(response.data.isQuorumSatisfied);
            });
        } catch (error) {
            console.error("Error while trying to call calcVotingAgendaitemQuorumState: ", error);
        }
    };

    getVotingResult = async () => {
        try {
            const response = await Axios.getAuthInstance().get(
                NetworkConfig.getVotingAgendaitemResult + this.selectedVoting.votingagendaitemid,
                {
                    urlWithoutParams: NetworkConfig.getVotingAgendaitemResult,
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                }
            );

            runInAction(() => {
                this.displayedVotingResult = {
                    votingagendaitemid: this.selectedVoting.votingagendaitemid || "",
                    resultText: response.data.result,
                    resultid: response.data.resultid,
                    votingaccepted: response.data.votingaccepted,
                    key1accepted: response.data.key1accepted,
                    key2accepted: response.data.key2accepted,
                    key1details: response.data.key1details,
                    key2details: response.data.key2details
                };
                this.setHasNoVotingOptions(false);
            });
        } catch (error) {
            console.error("Error while trying to call getVotingAgendaitemResult: ", error);
        }
    };

    loadVotingDetails = async (votingId: string, agendaitemId: string) => {
        try {
            const { data }: { data: GetVotingDetails } = await apolloClientInstance.query<
                GetVotingDetails,
                GetVotingDetailsVariables
            >({
                query: GET_VOTING_DETAILS,
                fetchPolicy: "no-cache",
                variables: {
                    agendaitem: agendaitemId,
                    votingid: votingId,
                    language: getLang()
                }
            });

            runInAction(() => {
                if (data.survey_votings && data.survey_votings.length > 0) {
                    this.updateVotingBasisField("votingId", votingId);
                    this.updateVotingBasisField("key1", data.survey_votings[0].key1id);
                    this.updateVotingBasisField("key2", data.survey_votings[0].key2id);
                    this.updateVotingBasisField("quorum", data.survey_votings[0].quorum);
                    this.updateVeto(data.survey_votings[0].veto || false);
                    this.updateVotingBasisField("votetype", data.survey_votings[0].votetype || VoteType.ARBITRAMENT);
                    this.updateVotingBasisField("majority", data.survey_votings[0].majority || 51);
                    this.updateEntirety(data.survey_votings[0].entirety || false);

                    if (data.survey_votings[0].voting_mls && data.survey_votings[0].voting_mls.length > 0) {
                        this.updateVotingBasisField("votingMlsId", data.survey_votings[0].voting_mls[0].id);
                    }

                    if (
                        data.survey_votings[0].votingagendaitems &&
                        data.survey_votings[0].votingagendaitems.length > 0 &&
                        data.survey_votings[0].votingagendaitems[0].votingagendaitemrealestates
                    ) {
                        const realestateIds =
                            data.survey_votings[0].votingagendaitems[0].votingagendaitemrealestates.map(
                                (r) => r.realestateid
                            );

                        this.setSelectedRealestateIds(realestateIds);
                    }
                    this.setQuorumAndKeysMls(
                        data.survey_votings[0].quorum,
                        data.survey_votings[0].key1id,
                        data.survey_votings[0].key2id
                    );
                }
                this.isVotingOptionsModified = false;
            });
        } catch (error) {
            console.error("Failed to fetch votings:", error);
            throw error;
        }
    };

    loadVotingTenantsAndOptions = async () => {
        const { votingid, votingagendaitemid } = this.selectedVoting;

        if (!votingid || !votingagendaitemid) {
            this.clearLiveVotingData();
            this.resetVotingLoadCache();
            return;
        }

        const contextKey = this.getVotingContextKey(votingid, votingagendaitemid);
        if (this.lastLoadedVotingContextKey === contextKey) return;

        this.setLastLoadedVotingContextKey(contextKey);

        try {
            const { data }: { data: GetVotingTenantsAndOptionsAndResults } = await apolloClientInstance.query<
                GetVotingTenantsAndOptionsAndResults,
                GetVotingTenantsAndOptionsAndResultsVariables
            >({
                query: GET_VOTING_TENANTS_OPTIONS_RESULTS,
                fetchPolicy: "no-cache",
                variables: {
                    votingagendaitemid,
                    votingid
                }
            });

            runInAction(() => {
                this.votingTenants = data.survey_v_votingtenantdetails.map((tenant) => ({
                    tenantid: tenant.tenantid ?? "",
                    name: tenant.name ?? "",
                    unitvalue: tenant.unitvalue ?? 0,
                    unitcount: tenant.unitcounter ?? 0
                }));

                this.votingOptions = data.survey_votingoptions
                    .filter((option) => option.votingoption_mls.length > 0)
                    .map((option) => {
                        const mls = option.votingoption_mls[0];
                        return {
                            votingoptionid: mls.votingoptionid ?? "",
                            text: mls.text,
                            sort: option.sort ?? null
                        };
                    })
                    .sort((a, b) => {
                        if (a.sort == null && b.sort == null) return 0;
                        if (a.sort == null) return 1;
                        if (b.sort == null) return -1;
                        return a.sort - b.sort;
                    });

                this.votingResults = data.survey_votingagendaitemresults.map((result) => ({
                    id: result.id ?? "",
                    state: result.state ?? 0,
                    tenantid: result.tenantid ?? "",
                    unitid: result.unitid ?? "",
                    votingagendaitemrealestateid: result.votingagendaitemrealestateid ?? "",
                    votingoptionid: result.votingoptionid
                }));

                // Check if all results have no selected option
                const hasNoVotingOptions =
                    this.votingResults.length > 0 && this.votingResults.every((r) => r.votingoptionid == null);

                this.setHasNoVotingOptions(hasNoVotingOptions);
            });
        } catch (error) {
            this.resetVotingLoadCache();
            console.error("Failed to fetch votings:", error);
            throw error;
        }
    };

    loadVotingOptions = async () => {
        try {
            const { data }: { data: GetVotingOptions } = await apolloClientInstance.query<
                GetVotingOptions,
                GetVotingOptionsVariables
            >({
                query: GET_VOTING_OPTIONS,
                fetchPolicy: "no-cache",
                variables: {
                    votingid: this.selectedVotingId
                }
            });

            runInAction(() => {
                this.votingOptions = data.survey_votingoptions
                    .filter((option) => option.votingoption_mls.length > 0)
                    .map((option) => {
                        const mls = option.votingoption_mls[0];
                        return {
                            votingoptionid: mls.votingoptionid ?? "",
                            text: mls.text,
                            sort: option.sort ?? null
                        };
                    })
                    .sort((a, b) => {
                        if (a.sort == null && b.sort == null) return 0;
                        if (a.sort == null) return 1;
                        if (b.sort == null) return -1;
                        return a.sort - b.sort;
                    });
            });
        } catch (error) {
            console.error("Failed to fetch votings:", error);
            throw error;
        }
    };

    loadAllVotingsByAgendaItemId = async (agendaitemId: string) => {
        // Stale guard: if the user clicks a different item before this response arrives, discard it
        this.lastLoadedAgendaitemId = agendaitemId;

        try {
            const { data }: { data: GetVotingsByAgendaitemId } = await apolloClientInstance.query<
                GetVotingsByAgendaitemId,
                GetVotingsByAgendaitemIdVariables
            >({
                query: GET_VOTINGS_BY_AGENDAITEMID,
                fetchPolicy: "no-cache",
                variables: {
                    agendaitemid: agendaitemId
                }
            });

            // Discard response if a newer agenda item was clicked while awaiting
            if (agendaitemId !== this.lastLoadedAgendaitemId) return;

            runInAction(() => {
                this.votingList = data.survey_v_votings_by_agendaitem.map((v) => ({
                    votingid: v.votingid,
                    votingagendaitemid: v.votingagendaitemid,
                    question: v.question ?? "",
                    title: v.title ?? ""
                }));

                if (this.votingList.length > 0) {
                    this.setSelectedVoting(this.votingList[0]);
                } else {
                    this.setSelectedVoting({
                        votingid: "",
                        votingagendaitemid: "",
                        question: "",
                        title: ""
                    });
                }
            });
        } catch (error) {
            console.error("Failed to fetch votings:", error);
            throw error;
        }
    };

    loadVotingInformationBasis = async () => {
        try {
            const { data }: { data: GetVotingInformationBasis } =
                await apolloClientInstance.query<
                GetVotingInformationBasis,
                GetVotingInformationBasisVariables
            >({
                query: GET_VOTING_INFORMATION_BASIS,
                fetchPolicy: "no-cache",
                variables: {
                    language: getLang()
                }
            });

            runInAction(() => {
                const validKeys: IKeys[] = data.common_key_mls.map((item) => ({
                    keyid: item.keyid ?? "",
                    name: item.name ?? "",
                    type: item.key?.type ?? 0
                }));

                this.setQuorums(this.rootStore.enumerationsStore.getEnumMapByType(ENUM_TYPE.QUORUMS));
                this.setKeys(validKeys);
            });
        } catch (error) {
            console.error("Failed to fetch votings:", error);
            throw error;
        }
    };

    updateQuorum = (quorumKey: number) => {
        this.votingBasis.quorum = quorumKey;
    };

    updateKey = (keyId: string) => {
        this.votingBasis.key1 = keyId;
    };

    updateVeto = (veto: boolean) => {
        this.votingBasis.veto = veto;
    };

    updateEntirety = (entirety: boolean) => {
        this.votingBasis.entirety = entirety;
    };

    updateVotingBasisField = <K extends keyof IVotingBasis>(field: K, value: IVotingBasis[K]) => {
        this.votingBasis[field] = value;

        if (field === "majority") {
            this.validateMajority();
        }
        if (field === "votetype") {
            this.isVotingOptionsModified = true;
        }
    };

    resetVotingBasis = () => {
        this.votingBasis = {
            votingId: "",
            title: "",
            question: "",
            quorum: QuorumType.SIMPLE_MAJORITY,
            key1: "",
            key2: "",
            veto: false,
            votingMlsId: "",
            votetype: VoteType.ARBITRAMENT,
            majority: 51,
            entirety: false
        };
    };

    resetAllVotingData = () => {
        this.resetVotingBasis();
        this.setVotingOptionsDefault(getArbitramentOptions());
        this.setSelectedRealestateIds([]);
        this.isVotingOptionsModified = false;
    };

    private getVotingContextKey = (votingId: string, votingAgendaitemId: string) => `${votingId}:${votingAgendaitemId}`;

    resetVotingLoadCache = () => {
        this.setLastLoadedVotingContextKey("");
    };

    clearLiveVotingData = () => {
        this.votingTenants = [];
        this.votingOptions = [];
        this.votingResults = [];
        this.votingComponentResponse = [];
        this.setHasNoVotingOptions(true);
        // Reset so getVotingResult/getVotingAgendaitemQuorumState are always re-fetched
        this.displayedVotingResult = { ...this.displayedVotingResult, votingagendaitemid: "" };
    };

    saveVotingInformationBasis = async () => {
        const isCreatingNew = this.votingBasis.votingId.length <= 0;

        if (isCreatingNew) {
            this.updateVotingBasisField("votingId", uuidv4());
            if (this.votingBasis.votingMlsId.length <= 0) {
                this.updateVotingBasisField("votingMlsId", uuidv4());
            }
        }

        const mlsId = this.votingBasis.votingMlsId;
        const mlsData = mlsId
            ? [
                  {
                      id: mlsId,
                      title: this.votingBasis.title,
                      question: this.votingBasis.question,
                      language: getLang()
                  }
              ]
            : [];

        const insertBody: InsertVotingInformationBasisVariables = {
            objects: [
                {
                    id: this.votingBasis.votingId,
                    key1id: this.votingBasis.key1 || null,
                    key2id: this.votingBasis.key2 || null,
                    quorum: this.votingBasis.quorum,
                    veto: this.votingBasis.veto,
                    majority: this.votingBasis.majority,
                    entirety: this.votingBasis.entirety,
                    voting_mls: {
                        data: mlsData,
                        on_conflict: {
                            constraint: survey_voting_mls_constraint.PK_voting_mls,
                            update_columns: [
                                survey_voting_mls_update_column.title,
                                survey_voting_mls_update_column.question
                            ]
                        }
                    }
                }
            ]
        };

        try {
            await apolloClientInstance.mutate<InsertVotingInformationBasis, InsertVotingInformationBasisVariables>({
                mutation: INSERT_VOTING_INFORMATION_BASIS,
                variables: insertBody
            });
        } catch (error: any) {
            console.error(i18next.t("screens.assemblies.vote.voting_save_failed: ", error));
        }
    };

    saveVotingOptions = async () => {
        const votingId = this.votingBasis.votingId;
        const votingOptions = (
            this.isNewVoting ? this.votingOptionsDefault : this.votingOptions.map((opt) => opt.text)
        ).filter(
            (text, index) => this.votingBasis.votetype === VoteType.ARBITRAMENT || index < 3 || text.trim().length > 0
        );
        const selectedRealestateIds = this.selectedRealestateIds;

        const agendaitemId = this.rootStore.agendaitemStore.currentAgendaItem?.id;
        const assemblyId = this.rootStore.assemblyStore.currentAssembly?.id;

        if (!votingId || !agendaitemId || !assemblyId) {
            console.error("Missing required data: votingId, agendaitemId or assemblyId");
            return;
        }

        const isVotetypeSaved = await this.saveVotetypeVoting();
        if (!isVotetypeSaved) {
            return;
        }

        const existingVotingagendaitemid = !this.isNewVoting
            ? this.votingList.find((v) => v.votingid === votingId)?.votingagendaitemid
            : undefined;
        const keepAgendaitemIds = existingVotingagendaitemid ? [existingVotingagendaitemid] : [];

        // For existing votings: skip the agendaitem insert entirely — the row already exists and
        const objectVotingAgendaitems = existingVotingagendaitemid
            ? []
            : [
                  {
                      votingid: votingId,
                      agendaitemid: agendaitemId,
                      assemblyid: assemblyId,
                      votingagendaitemrealestates: {
                          data: selectedRealestateIds.map((realestateid) => ({ realestateid }))
                      }
                  }
              ];

        const objectRealestates = existingVotingagendaitemid
            ? selectedRealestateIds.map((realestateid) => ({
                  votingagendaitemid: existingVotingagendaitemid,
                  realestateid
              }))
            : [];

        const insertBody = {
            votingid: votingId,
            keepAgendaitemIds,
            objectVotingOptions: votingOptions.map((text, index) => ({
                votingid: votingId,
                sort: index + 1,
                votingoption_mls: {
                    data: [
                        {
                            text,
                            language: getLang()
                        }
                    ]
                }
            })),
            objectVotingAgendaitems,
            objectRealestates
        };

        try {
            await apolloClientInstance.mutate<InsertVotingOptions, InsertVotingOptionsVariables>({
                mutation: INSERT_VOTING_OPTION_AGENDAITEM,
                variables: insertBody
            });

            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.vote.voting_save_success"),
                MessageType.SUCCESS
            );
            await this.loadAllVotingsByAgendaItemId(agendaitemId);
        } catch (error: any) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.vote.voting_save_failed"),
                MessageType.ERROR
            );
            console.error(i18next.t("screens.assemblies.vote.voting_save_failed: ", error));
            if (this.isNewVoting && this.votingBasis.votingId) {
                await this.deleteVotingById(this.votingBasis.votingId);
            }
        }
        this.resetAllVotingData();
    };

    saveVotetypeVoting = async (): Promise<boolean> => {
        const votingId = this.votingBasis.votingId;
        const votetype = this.votingBasis.votetype;

        if (!votingId) {
            console.error("Missing required data: votingId");
            return false;
        }

        try {
            await apolloClientInstance.mutate<UpdateVotetypeVotings, UpdateVotetypeVotingsVariables>({
                mutation: UPDATE_VOTETYPE_VOTINGS,
                variables: {
                    id: votingId,
                    votetype: votetype
                }
            });

            return true;
        } catch (error: any) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.vote.voting_save_failed"),
                MessageType.ERROR
            );
            console.error(i18next.t("screens.assemblies.vote.voting_save_failed: ", error));

            if (this.isNewVoting && this.votingBasis.votingId) {
                await this.deleteVotingById(this.votingBasis.votingId);
            }

            return false;
        }
    };

    deleteVotingById = async (votingid: string) => {
        try {
            const idToDelete = votingid ?? this.votingBasis.votingId;

            if (idToDelete !== undefined) {
                await apolloClientInstance.mutate<DeleteVoting, DeleteVotingVariables>({
                    mutation: DELETE_VOTING,
                    variables: { votingId: idToDelete }
                });

                runInAction(() => {
                    this.votingList = this.votingList.filter((voting) => voting.votingid !== idToDelete);
                });
            }
        } catch (error: any) {
            console.error("Voting failed to delete. Error: ", error);
        }
    };

    saveVotingResult = async () => {
        const votingOptions = this.votingOptions;
        const responses = this.votingComponentResponse;

        if (this.votingResults.length === 0 || votingOptions.length === 0) {
            console.error("Missing voting results or voting options.");
            return;
        }

        const updatedResults = this.votingResults.map((result) => {
            // Find the response for this tenant
            const response = responses.find((r) => r.id === result.tenantid);

            let votingoptionid: string | null = null;

            if (response && response.selectedColumn !== null) {
                const index = response.selectedColumn - 1;
                votingoptionid = votingOptions[index]?.votingoptionid ?? null;
            }

            return {
                ...result,
                votingoptionid
            };
        });

        const hasInvalidResultRows = updatedResults.some(
            (result) => !result.id || !result.tenantid || !result.unitid || !result.votingagendaitemrealestateid
        );

        if (hasInvalidResultRows) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.vote.voting_result_failed"),
                MessageType.ERROR
            );
            console.error("Aborted saveVotingResult due to stale voting result rows. Reloading voting data.");

            this.resetVotingLoadCache();
            await this.loadVotingTenantsAndOptions();
            return;
        }

        const insertBody = {
            votingagendaitemresults: updatedResults
        };

        try {
            await apolloClientInstance.mutate<UpsertVotingAgendaitemResult, UpsertVotingAgendaitemResultVariables>({
                mutation: UPSERT_VOTING_AGENDA_RESULTS,
                variables: insertBody
            });

            runInAction(() => {
                this.votingResults = updatedResults;
                this.votingComponentResponse = [];

                this.getVotingAgendaitemQuorumState();
                this.getVotingResult();
            });

            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.vote.voting_result_success"),
                MessageType.SUCCESS
            );
        } catch (error: any) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.vote.voting_result_failed"),
                MessageType.ERROR
            );
            console.error("Error setTenantVotingResult: ", error);
        }
    };

    /* SETTERS */

    setIsVotingModalDisplayed = (isVotingModalDisplayed: boolean) => {
        this.isVotingModalDisplayed = isVotingModalDisplayed;
    };

    setQuorums = (quorums: Map<number, { label: string }>) => {
        this.quorums = quorums;
    };

    setKeys = (keys: IKeys[]) => {
        this.keys = keys;
    };

    setVotingOptionsDefault = (votingOptionsDefault: string[]) => {
        this.votingOptionsDefault = votingOptionsDefault;
    };

    setVotingOptions = (votingOptions: IVotingOptions[]) => {
        this.isVotingOptionsModified = true;
        this.votingOptions = votingOptions;
    };

    setSelectedRealestateIds = (selectedRealestateIds: string[]) => {
        this.isVotingOptionsModified = true;
        this.selectedRealestateIds = selectedRealestateIds;
    };

    setSelectedVoting = (selectedVoting: IVoting) => {
        if (this.selectedVoting.votingid === selectedVoting.votingid) return;
        this.selectedVoting = selectedVoting;
        this.resetVotingLoadCache();
        this.clearLiveVotingData();
        this.tenantListForQuorumCheck = [];
        this.selectedVotingRefreshKey++;
    };

    setVotingComponentResponse = (response: { id: string; selectedColumn: number | null }[]) => {
        this.votingComponentResponse = response;
    };

    setIsNewVoting = (isNewVoting: boolean) => {
        this.isNewVoting = isNewVoting;
    };

    setSelectedVotingId = (selectedVotingId: string) => {
        this.selectedVotingId = selectedVotingId;
    };

    setQuorumAndKeysMls = (quorum: number, key1: string, key2?: string) => {
        this.quorumMls = this.quorums.get(quorum)?.label || "";
        this.key1Mls = this.keys.find((k) => k.keyid === key1)?.name || "";
        this.key2Mls = key2 ? this.keys.find((k) => k.keyid === key2)?.name || "" : "";
    };

    setIsVotingResultTie = (isVotingResultTie: boolean) => {
        this.isVotingResultTie = isVotingResultTie;
    };

    setVotingResultText = (votingResultText: string) => {
        this.votingResultText = votingResultText;
    };

    setIsVotingAccepted = (isVotingAccepted: boolean) => {
        this.isVotingAccepted = isVotingAccepted;
    };

    setTenantActive(tenantId: string, active: boolean) {
        const nextState = active ? IAttendanceState.PRESENT : IAttendanceState.ABSENT;

        // Update ALL rows for this tenantId
        for (const vr of this.votingResults) {
            if (vr.tenantid === tenantId && vr.state !== nextState) {
                vr.state = nextState;
            }
        }
    }

    setHasNoVotingOptions = (hasNoVotingOptions: boolean) => {
        this.hasNoVotingOptions = hasNoVotingOptions;
    };

    setIsQuoratePerVoting = (isQuoratePerVoting: boolean) => {
        this.isQuoratePerVoting = isQuoratePerVoting;
    };

    setTenantListForQuorumCheck = (tenantid: string, state: number) => {
        // Check if tenant already exists in the list
        const index = this.tenantListForQuorumCheck.findIndex((t) => t.tenantid === tenantid);
        if (index !== -1) {
            // Update existing tenant state
            this.tenantListForQuorumCheck[index].state = state;
        } else {
            // Add new tenant to the list
            this.tenantListForQuorumCheck.push({ tenantid, state });
        }
    };

    setLastLoadedVotingContextKey = (key: string) => {
        this.lastLoadedVotingContextKey = key;
    };
}
